/**
 * Output placeholder descriptions and example values for the inline-config popover.
 *
 * Used by NodeOutputPlaceholderChipInput via `componentType` (+ optional `variant`).
 * Any component can opt in; board components are defined here too so all
 * shapes live in one place.
 */

const TYPE_DESCRIPTIONS = {
  text: 'A plain text value.',
  number: 'A numeric value.',
  json: 'A JSON object serialized as a string. Parse it downstream to read nested fields.',
  list: 'An array of values. Use in a loop step or join them into text.',
  datetime: 'An ISO 8601 date/time string.',
  html: 'HTML content as a string.',
  image: 'An image URL or file reference.',
  global: 'A global workflow variable value.',
  form_placeholder: 'A form field answer value.',
};

const TYPE_SAMPLES = {
  text: '"example value"',
  number: '42',
  json: `{
  "key": "value",
  "nested": { "count": 3 }
}`,
  list: `[
  "first item",
  "second item"
]`,
  datetime: '"2026-06-30T18:00:00.000Z"',
  html: '"<p>Rich text content</p>"',
  image: '"https://files.example.com/image.png"',
  global: '"my_global_value"',
  form_placeholder: '"answer text"',
};

/** Generic info keyed by placeholder chip type (text/json/list/…). */
export function getOutputInfoByPlaceholderType(placeholderType) {
  const type = placeholderType || 'text';
  return {
    description: TYPE_DESCRIPTIONS[type] || TYPE_DESCRIPTIONS.text,
    sample: TYPE_SAMPLES[type] || TYPE_SAMPLES.text,
  };
}

/** Components whose output shape matches the placeholder chip type — no custom copy needed. */
export const PLACEHOLDER_TYPE_ONLY_COMPONENTS = new Set([
  'code',
  'advanced_code',
  'custom_function',
  'ai_assistant',
  'ai_agent',
  'session_variables',
  'trigger_via_email',
  'dynamic_form_field',
  'form_field',
  'bulk_assessment_field',
]);

/** Sub-output types inside multi-output inline configs (not the store's primary type). */
export const SUB_OUTPUT_COMPONENT_TYPE = {
  FORM_FIELD: 'form_field',
  DYNAMIC_FORM_FIELD: 'dynamic_form_field',
  BULK_ASSESSMENT_FIELD: 'bulk_assessment_field',
  ARTIFACT_CREATION_URL: 'artifact_creation_url',
  ARTIFACT_SUBMISSION: 'artifact_submission',
  AI_AGENT_SESSION_URL: 'ai_agent_session_url',
  FORM_STEP_URL: 'form_step_url',
  DYNAMIC_REVIEW_TASK_URL: 'dynamic_review_task_url',
};

/** Variant keys for multi-mode components — shared by inline configs and this registry. */
export const LIST_TRANSFORM_VARIANT = {
  JOIN: 'join',
  COUNT: 'count',
  PRESENT: 'present',
  SPLIT_ITEM: 'split_item',
};

export const API_REQUEST_VARIANT = {
  RAW: 'raw',
  PARSED: 'parsed',
};

export const QUERY_DATABASE_VARIANT = {
  STRING: 'string',
  LIST: 'list',
};

/**
 * Component-specific output shapes. Keys match workflow component `type` strings.
 * Values may be static objects or `(variant) => object` for multi-mode components.
 */
export const COMPONENT_OUTPUT_INFO = {
  create_board_ticket: {
    description:
      "The new ticket's ID (text). Use it downstream to get, update, or link to the ticket you just created.",
    sample: '"a1b2c3d4-5e6f-7890-abcd-1234567890ab"',
  },
  update_board_ticket: {
    description:
      "The updated ticket's ID (text). Use it downstream to chain further actions on the same ticket.",
    sample: '"a1b2c3d4-5e6f-7890-abcd-1234567890ab"',
  },
  get_board_ticket: {
    description:
      'The full ticket as JSON. Parse it downstream (in an AI or code step) to read any value.',
    sample: `{
  "ticketId": "a1b2c3d4-5e6f-7890-abcd-1234567890ab",
  "ticketCode": "SUP-42",
  "title": "Refund request",
  "boardId": "board_9f83",
  "currentStageId": "in_review",
  "status": "OPEN",
  "assigneeEmail": "sam@acme.com",
  "fields": { "priority": "High", "amount": 250 },
  "createdAt": "2026-06-30T18:00:00Z",
  "updatedAt": "2026-06-30T18:05:00Z"
}`,
  },
  trigger_on_board_ticket: {
    description:
      'The ticket that changed, as JSON with a `new` (after) and `old` (before) snapshot. `old` is null when a ticket is first created.',
    sample: `{
  "new": {
    "ticketId": "a1b2c3d4-5e6f-7890-abcd-1234567890ab",
    "status": "DONE",
    "currentStageId": "closed",
    "fields": { "priority": "High" }
  },
  "old": {
    "ticketId": "a1b2c3d4-5e6f-7890-abcd-1234567890ab",
    "status": "OPEN",
    "currentStageId": "in_review",
    "fields": { "priority": "Low" }
  }
}`,
  },

  create_database_record: {
    description:
      'The new row ID (text). Use it downstream to query or update the same record.',
    sample: '"row_7f3a9c2e1b"',
  },
  update_database_record: {
    description:
      'The updated row ID (text). Use it downstream to reference the same record.',
    sample: '"row_7f3a9c2e1b"',
  },
  create_row_v2: {
    description:
      'The new row ID (text). Use it downstream to query or update the same row.',
    sample: '"row_7f3a9c2e1b"',
  },

  create_file: {
    description:
      'The created file ID (text). Use it downstream in file or document steps.',
    sample: '"file_8d2e4a1c"',
  },
  document_editor: {
    description:
      'The generated document file ID (text). Use it downstream to download or attach the document.',
    sample: '"file_doc_9a1b2c3d"',
  },

  http_request: {
    description:
      'The full HTTP response as JSON: status, headers, and body. Parse the body field downstream.',
    sample: `{
  "status": 200,
  "statusText": "OK",
  "headers": { "content-type": "application/json" },
  "body": "{\\"id\\":123,\\"name\\":\\"Acme\\"}"
}`,
  },

  webhook_trigger: {
    description:
      'The incoming webhook payload as JSON. Fields depend on what the caller sends.',
    sample: `{
  "event": "order.created",
  "data": { "orderId": "ord_123", "total": 99.5 }
}`,
  },

  await_external_event: {
    description:
      'The external event payload as JSON. Shape matches the event schema you configured.',
    sample: `{
  "eventType": "payment.received",
  "payload": { "amount": 150, "currency": "USD" }
}`,
  },

  trigger_from_api_polling: {
    description:
      'Each polled API response as JSON. Use when the poll finds new or changed data.',
    sample: `{
  "id": "rec_456",
  "status": "active",
  "updatedAt": "2026-06-30T18:00:00Z"
}`,
  },

  trigger_on_database_update: {
    description:
      'The changed row as JSON with `new` (after) and `old` (before) snapshots. `old` is null on row creation.',
    sample: `{
  "new": { "rowId": "row_7f3a", "status": "Approved", "amount": 500 },
  "old": { "rowId": "row_7f3a", "status": "Pending", "amount": 500 }
}`,
  },

  trigger_table_v2: {
    description:
      'The triggered row ID (text). Use it downstream to query or update the same row.',
    sample: '"row_7f3a9c2e1b"',
  },

  trigger_on_artifact: {
    description:
      'The submitted artifact event (JSON): the event name, submitted payload, actor, and artifact identifiers.',
    sample: `{
  "artifactInstanceId": "art_1a2b3c",
  "artifactDefinitionId": "artdef_9z8y",
  "eventName": "submitted",
  "payload": { "rating": 5, "comment": "Looks good" },
  "actor": { "email": "user@example.com" },
  "title": "Feedback form",
  "submittedAt": "2026-07-06T17:00:00Z"
}`,
  },

  trigger_from_external_app: {
    description:
      'The external app event payload as JSON. Fields depend on the connected app and trigger.',
    sample: `{
  "id": "evt_789",
  "type": "new_lead",
  "properties": { "email": "lead@acme.com" }
}`,
  },

  perform_action_in_external_app: {
    description:
      'The external app action result as JSON. Fields depend on the action you configured.',
    sample: `{
  "success": true,
  "id": "act_321",
  "result": { "message": "Created" }
}`,
  },

  converge_threads: {
    description:
      'Merged results from all converged parallel branches as JSON. Keys are branch output placeholder names.',
    sample: `{
  "branch_a_result": "done",
  "branch_b_result": { "count": 3 }
}`,
  },

  schedule_start: {
    description:
      'The scheduled start time as an ISO datetime string (when the workflow actually started).',
    sample: '"2026-06-30T09:00:00.000Z"',
  },
  time_delay: {
    description:
      'The time the delay completed as an ISO datetime string (when execution resumed).',
    sample: '"2026-06-30T09:05:00.000Z"',
  },

  loop_through_list: {
    description:
      'The current list item (text) for this iteration. Changes on each loop pass.',
    sample: '"current list item"',
  },

  webhook: {
    description:
      'A field extracted from the webhook sample payload (text). One placeholder per mapped field.',
    sample: '"field value from webhook"',
  },

  conditional_logic: {
    description:
      'The result of this condition block: `"true"` or `"false"` (text), depending on whether the logic matched.',
    sample: '"true"',
  },
  conditional_workflow: {
    description:
      'The result of this condition block: `"true"` or `"false"` (text), depending on whether the logic matched.',
    sample: '"true"',
  },

  parse_json: {
    description:
      'A value extracted from the parsed JSON (text). One placeholder per mapped path.',
    sample: '"extracted value"',
  },

  query_database_record: (variant) =>
    variant === QUERY_DATABASE_VARIANT.STRING
      ? {
          description: 'Matching rows joined into a single text string.',
          sample: '"row1, row2, row3"',
        }
      : {
          description: 'Matching rows as a list of row objects.',
          sample: `[
  { "rowId": "row_1", "name": "Alice" },
  { "rowId": "row_2", "name": "Bob" }
]`,
        },

  query_table_v2: (variant) =>
    variant === QUERY_DATABASE_VARIANT.STRING
      ? {
          description: 'Matching rows joined into a single text string.',
          sample: '"value1, value2"',
        }
      : {
          description: 'Matching rows as a list. Each item is a row or column value depending on your query.',
          sample: `[
  "row_7f3a9c2e1b",
  "row_8e4b0d3f2c"
]`,
        },

  list_transform: (variant) => {
    if (variant === LIST_TRANSFORM_VARIANT.SPLIT_ITEM) {
      return {
        description: 'One item from a split list (text). One placeholder per split slot.',
        sample: '"list item at this index"',
      };
    }
    if (
      variant === LIST_TRANSFORM_VARIANT.COUNT
      || variant === LIST_TRANSFORM_VARIANT.PRESENT
    ) {
      return {
        description: 'The numeric result of the transform (count or present check).',
        sample: '3',
      };
    }
    if (variant === LIST_TRANSFORM_VARIANT.JOIN) {
      return {
        description: 'List items joined into a single text string.',
        sample: '"item1, item2, item3"',
      };
    }
    return getOutputInfoByPlaceholderType(variant || 'list');
  },

  bulk_assessment: {
    description:
      'Assessment results as JSON. Nested keys under this placeholder hold per-field scores (see field placeholders below).',
    sample: `{
  "overallScore": 85,
  "recommendation": "Approve"
}`,
  },

  api_request_and_response: (variant) =>
    variant === API_REQUEST_VARIANT.RAW
      ? {
          description: 'The raw API response body as JSON (unparsed).',
          sample: `{
  "id": 123,
  "status": "complete",
  "items": [1, 2, 3]
}`,
        }
      : {
          description: 'A field extracted from the API response (text). One placeholder per mapped response field.',
          sample: '"extracted response value"',
        },

  ai_agent_session_url: {
    description:
      'The agent session URL (text). Available on the creation port before the agent finishes.',
    sample: '"https://app.workflow86.com/agent/session/abc123"',
  },

  calculator: (variant) =>
    variant === 'datetime'
      ? getOutputInfoByPlaceholderType('datetime')
      : getOutputInfoByPlaceholderType('number'),

  dynamic_review_task_url: {
    description:
      'The review task URL (text). Available on the creation port when the task is assigned.',
    sample: '"https://app.workflow86.com/tasks/review/abc123"',
  },
  form_step_url: {
    description:
      'The form URL (text). Available on the creation port when the form is sent.',
    sample: '"https://form.workflow86.com/f/abc123"',
  },

  artifact_creation_url: {
    description:
      'The artifact URL (text). Available on the creation port when the artifact is published.',
    sample: '"https://app.workflow86.com/artifacts/abc123"',
  },
  artifact_submission: {
    description:
      'The artifact submission event as JSON: instance ID, event name, payload, actor, and timestamp.',
    sample: `{
  "artifactInstanceId": "inst_456",
  "eventName": "submitted",
  "payload": { "approved": true },
  "actor": { "email": "reviewer@acme.com" },
  "submittedAt": "2026-06-30T18:00:00Z"
}`,
  },
};

// v2 webhook trigger shares the same output shape as v1.
COMPONENT_OUTPUT_INFO.webhook_trigger_v2 = COMPONENT_OUTPUT_INFO.webhook_trigger;

/**
 * Resolve output popover content for a component or placeholder type.
 *
 * @param {{ componentType?: string, placeholderType?: string, variant?: string }} opts
 * @returns {{ description: string, sample: string } | undefined}
 */
export function getOutputInfo({ componentType, placeholderType, variant } = {}) {
  if (componentType) {
    const entry = COMPONENT_OUTPUT_INFO[componentType];
    if (entry) {
      return typeof entry === 'function' ? entry(variant) : entry;
    }
    if (PLACEHOLDER_TYPE_ONLY_COMPONENTS.has(componentType)) {
      return getOutputInfoByPlaceholderType(variant || placeholderType || 'text');
    }
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[outputPlaceholderInfo] Unknown componentType "${componentType}" — falling back to placeholder type`,
      );
    }
  }
  if (placeholderType) {
    return getOutputInfoByPlaceholderType(placeholderType);
  }
  return undefined;
}
